<template>
	<div
		v-if="appearance === 'card'"
		class="stock-info-card"
	>
		<div class="flex items-center flex-row lg:gap-3 gap-2">
			<span
				v-if="failedImages.has(symbol)"
				class="min-w-6 w-6 lg:min-w-10 lg:w-10 w-6 lg:h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
				style="background-color: #222"
			>
				{{ symbol }}
			</span>
			<img
				v-else
				:src="`https://stt-ts-resources.s3.us-east-1.amazonaws.com/stock-symbols/${symbol}`"
				alt="logo"
				class="min-w-6 w-6 lg:min-w-10 lg:w-10 w-6 lg:h-10 rounded-full bg-white border border-slate-25"
				@error="handleImageError(symbol)"
			/>
			<div class="flex flex-col">
				<div class="font-regular text-base text-black">{{ symbol }}</div>
				<div
					class="text-slate-400 text-xs overflow-hidden text-ellipsis whitespace-nowrap max-w-[88px]"
				>
					{{ name }}
				</div>
			</div>
		</div>
		<top-gainer-chart
			v-if="prices && prices.length"
			:data="{ labels, prices }"
			:current-net-change="net_chg"
			class="w-full h-[40px] lg:h-[66px]"
		/>
		<div class="flex justify-between items-end">
			<span class="text-black text-base font-regular">
				${{ price?.toFixed(2) || 0 }}
			</span>
			<span
				:class="[
					'text-sm',
					pc_chg && pc_chg >= 0 ? 'text-success' : 'text-negative'
				]"
			>
				{{ pc_chg && pc_chg >= 0 ? "+" : "" }}{{ pc_chg?.toFixed(2) || 0 }}%
			</span>
		</div>
	</div>

	<div
		v-else
		:class="[
			'flex justify-between items-center w-full max-w-md bg-transparent text-white border-b py-3',
			theme === 'dark'
				? 'border-primary-200/[0.2] hover:bg-primary-100/[0.1]'
				: 'border-slate-200 hover:bg-slate-25'
		]"
	>
		<div :class="[size === 'sm' ? 'w-[68px]' : 'w-[140px]']">
			<p
				:class="[
					'font-semibold text-sm leading-tight',
					theme === 'dark' ? 'text-primary-200' : 'text-black'
				]"
			>
				{{ symbol }}
			</p>
			<p
				:class="[
					'text-sm overflow-hidden text-ellipsis whitespace-nowrap max-w-[130px]',
					theme === 'dark' ? 'text-slate-200' : 'text-slate-400'
				]"
			>
				{{ name }}
			</p>
		</div>
		<div
			:class="['text-right ', size === 'sm' ? '' : 'min-w-[112px] ml-3']"
			v-if="!prices || prices.length === 0"
		/>

		<top-gainer-chart
			v-else
			:class="[size === 'sm' ? 'max-w-[68px] h-[40px]' : 'ml-auto']"
			:current-net-change="net_chg"
			:data="{
				labels,
				prices
			}"
		/>

		<div :class="['text-right ml-3 ', size === 'sm' ? '' : 'min-w-[112px]']">
			<p
				:class="[
					'text-sm font-semibold',
					theme === 'dark' ? 'text-white' : 'text-black'
				]"
			>
				{{ price?.toFixed(2) || 0 }}
			</p>
			<p
				class="text-sm"
				:class="{
					'text-success': pc_chg && pc_chg >= 0,
					'text-negative': pc_chg && pc_chg <= 0,
					'text-slate-400': !pc_chg || pc_chg == 0
				}"
			>
				{{ net_chg?.toFixed(2) }}
				<span>({{ pc_chg?.toFixed(2) || 0 }}%)</span>
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
import TopGainerChart from "./top-gainer-chart.vue";

defineProps({
	size: {
		type: String,
		default: "md"
	},
	theme: {
		type: String,
		default: "dark"
	},
	symbol: {
		type: String
	},
	name: {
		type: String
	},
	pc_chg: {
		type: Number
	},
	net_chg: {
		type: Number
	},
	price: {
		type: Number
	},
	labels: {
		type: Array,
		required: true
	},
	prices: {
		type: Array,
		required: true
	},
	appearance: {
		type: String,
		default: "inline" // 'inline' or 'card'
	}
});

// Handle failed image loading to prevent infinite retries - matching search bar pattern
const failedImages = ref(new Set<string>());

const handleImageError = (symbol: string) => {
	failedImages.value.add(symbol);
};
</script>

<style scoped>
.stock-info-card {
	@apply rounded-2xl bg-slate-100/[0.2] hover:bg-slate-100/[0.4] p-4 flex flex-col justify-between gap-2 h-full;
}
</style>
